import { IdleActivityDefaults, IdleActivityOptions } from '../types'

const DEFAULT_EVENTS = [
  'click',
  'mousemove',
  'mouseenter',
  'keydown',
  'scroll',
  'touchstart'
]

export const IDLE_ACTIVITY_DEFAULTS: IdleActivityDefaults = {
  storageKey: 'idle-activity:last-activity',
  idleMs: 60 * 60 * 1000,
  checkMs: 30 * 1000,
  throttleMs: 1000,
  events: DEFAULT_EVENTS
}

/**
 * Multi-tab idle detector.
 *
 * Activity in any tab updates a shared localStorage timestamp. Every open tab
 * periodically reads that value, so an active tab keeps background tabs from
 * timing out early.
 */
export class IdleActivity {
  private storageKey: string
  private idleMs: number
  private checkMs: number
  private throttleMs: number
  private events: string[]
  private onIdleCallback: (() => void) | null
  private isEnabled: () => boolean

  private checkTimer: number | null = null
  private lastWrite = 0
  private started = false
  private expired = false

  private readonly onInteraction = () => this.touch()
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.check()
    }
  }
  private readonly onStorage = (event: StorageEvent) => {
    if (event.key !== this.storageKey) return
    if (event.newValue) {
      this.lastWrite = Number(event.newValue) || 0
    }
  }

  constructor (options: IdleActivityOptions = {}) {
    this.storageKey = options.storageKey || IDLE_ACTIVITY_DEFAULTS.storageKey
    this.idleMs = options.idleMs || IDLE_ACTIVITY_DEFAULTS.idleMs
    this.checkMs = options.checkMs || IDLE_ACTIVITY_DEFAULTS.checkMs
    this.throttleMs = options.throttleMs || IDLE_ACTIVITY_DEFAULTS.throttleMs
    this.events = options.events || IDLE_ACTIVITY_DEFAULTS.events
    this.onIdleCallback = options.onIdle || null
    this.isEnabled = options.isEnabled || (() => true)
  }

  within (timeout: number, factor = 60000): this {
    this.idleMs = timeout * factor
    return this
  }

  every (checkMs: number): this {
    this.checkMs = checkMs
    return this
  }

  withStorageKey (storageKey: string): this {
    this.storageKey = storageKey
    return this
  }

  whenEnabled (isEnabled: () => boolean): this {
    this.isEnabled = isEnabled
    return this
  }

  do (callback: () => void): this {
    this.onIdleCallback = callback
    return this
  }

  start (): this {
    if (typeof window === 'undefined' || this.started) return this

    this.started = true
    this.expired = false
    this.touch(true)

    for (const event of this.events) {
      document.addEventListener(event, this.onInteraction, { passive: true })
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange)
    window.addEventListener('storage', this.onStorage)

    this.checkTimer = window.setInterval(() => this.check(), this.checkMs)
    return this
  }

  stop (): this {
    if (!this.started) return this

    this.started = false

    for (const event of this.events) {
      document.removeEventListener(event, this.onInteraction)
    }

    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    window.removeEventListener('storage', this.onStorage)

    if (this.checkTimer !== null) {
      window.clearInterval(this.checkTimer)
      this.checkTimer = null
    }

    return this
  }

  /** Record activity. Pass force=true to bypass the write throttle. */
  touch (force = false): void {
    if (typeof window === 'undefined' || !this.isEnabled() || this.expired) return

    const now = Date.now()
    if (!force && now - this.lastWrite < this.throttleMs) return

    this.lastWrite = now
    try {
      window.localStorage.setItem(this.storageKey, String(now))
    } catch (e) {
      // Safari private mode / quota errors — keep in-memory lastWrite only
    }
  }

  getLastActivity (): number {
    if (typeof window === 'undefined') return 0

    try {
      const stored = Number(window.localStorage.getItem(this.storageKey) || 0)
      return stored || this.lastWrite || 0
    } catch (e) {
      return this.lastWrite || 0
    }
  }

  isIdle (): boolean {
    const last = this.getLastActivity()
    if (!last) return true
    return Date.now() - last >= this.idleMs
  }

  clear (): void {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem(this.storageKey)
    } catch (e) {
      // ignore
    }
    this.lastWrite = 0
  }

  private check (): void {
    if (!this.isEnabled() || this.expired || !this.started) return

    if (this.isIdle()) {
      this.expired = true
      this.stop()
      if (this.onIdleCallback) {
        this.onIdleCallback()
      }
    }
  }
}
